import type { AdminColumn, HostColumn } from '@/mocks/adminLiveChat';
import { adminColumnLabels, adminColumnIcons, hostColumnLabels, hostColumnIcons } from '@/mocks/adminLiveChat';

interface AdminFiltersProps {
  mode: 'admin';
  activeColumn: AdminColumn;
  onChangeColumn: (col: AdminColumn) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  columnCounts: Record<AdminColumn, number>;
  totalNew: number;
}

interface HostFiltersProps {
  mode: 'host';
  activeColumn: HostColumn;
  onChangeColumn: (col: HostColumn) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  columnCounts: Record<HostColumn, number>;
  totalNew: number;
}

type Props = AdminFiltersProps | HostFiltersProps;

const adminColumns: AdminColumn[] = ['todos', 'cabina', 'live', 'concursos', 'favoritos', 'revision'];
const hostColumns: HostColumn[] = ['importantes', 'favoritos', 'saludos', 'canciones', 'pendientes'];

export default function UnifiedFilters(props: Props) {
  const { mode, activeColumn, onChangeColumn, searchQuery, onSearchChange, columnCounts, totalNew } = props;

  const columns = mode === 'admin' ? adminColumns : hostColumns;
  const columnLabels = mode === 'admin' ? adminColumnLabels : hostColumnLabels;
  const columnIcons = mode === 'admin' ? adminColumnIcons : hostColumnIcons;

  return (
    <div className="shrink-0 px-4 py-3 border-b border-background-200 bg-background-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <h3 className="text-sm font-bold text-foreground-950">
          {mode === 'admin' ? 'Chat Unificado' : 'Lista del Locutor'}
        </h3>
        <div className="flex items-center gap-1.5">
          {totalNew > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold">
              {totalNew} nuevos
            </span>
          )}
          <button
            className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
            title="Actualizar"
          >
            <i className="ri-refresh-line text-foreground-500 text-sm"></i>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-2.5">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar mensajes..."
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-background-100 text-foreground-800 text-xs border border-background-200 focus:border-secondary-300 focus:ring-1 focus:ring-secondary-200 outline-none transition-all placeholder:text-foreground-400"
        />
        {searchQuery && (
          <button
            onClick={() => onSearchChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-foreground-300 flex items-center justify-center cursor-pointer hover:bg-foreground-400 transition-colors"
          >
            <i className="ri-close-line text-background-50 text-[8px]"></i>
          </button>
        )}
      </div>

      {/* Column pills */}
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-0.5">
        {columns.map((col) => {
          const isActive = activeColumn === col;
          const count = columnCounts[col as keyof typeof columnCounts] ?? 0;
          return (
            <button
              key={col}
              onClick={() => onChangeColumn(col as never)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap cursor-pointer transition-all shrink-0 ${
                isActive
                  ? 'bg-secondary-500 text-background-50'
                  : 'bg-background-100 text-foreground-500 hover:bg-background-200'
              }`}
            >
              <i className={`${columnIcons[col as keyof typeof columnIcons]} ${isActive ? 'text-background-50' : ''} text-[11px]`}></i>
              {columnLabels[col as keyof typeof columnLabels]}
              {count > 0 && (
                <span className={`ml-0.5 px-1 py-0 text-[9px] rounded-full font-bold ${
                  isActive ? 'bg-background-50/30 text-background-50' : 'bg-background-200 text-foreground-500'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}